import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import './RouteProgress.css';

const ProgressCtx = createContext({ start: () => {}, done: () => {} });

export function RouteProgressProvider({ children }) {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const timers = useRef({ tick: null, hide: null, count: 0 });

  const clearTimers = () => {
    if (timers.current.tick) { clearInterval(timers.current.tick); timers.current.tick = null; }
    if (timers.current.hide) { clearTimeout(timers.current.hide); timers.current.hide = null; }
  };

  const start = useCallback(() => {
    timers.current.count += 1;
    clearTimers();
    setVisible(true);
    setProgress(8);
    timers.current.tick = setInterval(() => {
      setProgress((p) => {
        if (p >= 90) return p;
        const inc = p < 30 ? 6 : p < 60 ? 3 : p < 80 ? 1.2 : 0.4;
        return Math.min(90, p + inc);
      });
    }, 180);
  }, []);

  const done = useCallback(() => {
    timers.current.count = Math.max(0, timers.current.count - 1);
    if (timers.current.count > 0) return;
    clearTimers();
    setProgress(100);
    timers.current.hide = setTimeout(() => {
      setVisible(false);
      setProgress(0);
    }, 280);
  }, []);

  useEffect(() => () => clearTimers(), []);

  return (
    <ProgressCtx.Provider value={{ start, done }}>
      <div
        className={`route-progress ${visible ? 'is-visible' : ''} ${progress >= 100 ? 'is-done' : ''}`}
        aria-hidden={!visible}
      >
        <div className="route-progress__bar" style={{ width: `${progress}%` }}>
          <div className="route-progress__peg" />
        </div>
      </div>
      {children}
    </ProgressCtx.Provider>
  );
}

export const useRouteProgress = () => useContext(ProgressCtx);

// Auto-trigger on URL change (covers all pushes / replaces / pop).
export function RouteChangeTrigger() {
  const location = useLocation();
  const navType = useNavigationType();
  const { start, done } = useRouteProgress();
  const first = useRef(true);

  useEffect(() => {
    if (first.current) { first.current = false; return; }
    start();
    const t = setTimeout(done, 420);
    return () => clearTimeout(t);
  }, [location.pathname, location.search, navType, start, done]);

  return null;
}
